import { blinkConstants, CONNECTOR_DISPLAY_NAME, } from '@thoughtspot/blink-constants';
import { translate as t, translate } from '@thoughtspot/i18n';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { getTranslatedInfoForMessageCode } from './error-strings-mapping';
function getErrorObjectFromGraphQLErrors(graphqlError, query, errorMappingContext) {
    const nwError = graphqlError?.networkError;
    if (!nwError?.result?.errors &&
        _.isEmpty(graphqlError?.graphQLErrors)) {
        return [
            {
                queryName: query?.definitions
                    ?.map((def) => def?.name?.value || '')
                    .join(','),
                code: 'NW_ERROR',
                message: '',
                errorObj: nwError,
                errorMappingContext,
            },
        ];
    }
    const errs = graphqlError?.networkError?.result?.errors ||
        graphqlError?.graphQLErrors;
    if (!errs)
        return undefined;
    const errors = errs.map(e => {
        return {
            queryName: query?.definitions
                ?.map((def) => def?.name?.value || '')
                .join(','),
            code: e?.extensions?.code || 'ERROR_UNKNOWN_ERROR',
            message: e?.message,
            errorObj: e,
            errorMappingContext,
        };
    });
    return errors;
}
const getErrorMessage = (error, genericMessage) => {
    let errorMessage = '';
    const errorObjects = getErrorObjectFromGraphQLErrors(error);
    const errorCode = errorObjects[0]?.errorObj?.extensions?.exception?.upstreamResponse?.data?.code?.toString();
    if (errorCode) {
        errorMessage = getTranslatedInfoForMessageCode(errorCode)?.summary;
    }
    if (errorCode === '13003') {
        errorMessage = `${genericMessage} ${translate('alert.callosumErrorMessages.OBJECT_NOT_FOUND')}`;
    }
    return errorMessage ? translate(errorMessage) : genericMessage;
};
function getErrorCodeFromGraphQLErrors(graphqlError) {
    const errorObj = getErrorObjectFromGraphQLErrors(graphqlError);
    const errorCode = errorObj[0]?.errorObj?.extensions?.exception?.upstreamResponse?.data
        ?.code;
    return errorCode;
}
const containsError = (errorList, errorCodes) => {
    if (!(errorList && errorCodes))
        return false;
    if (errorCodes.indexOf('*') !== -1) {
        return true;
    }
    if (errorList.some(e => errorCodes.includes(e.code))) {
        return true;
    }
    return false;
};
const containsInApolloError = (error, errorCodes) => {
    if (!(error && errorCodes))
        return false;
    const errorList = getErrorObjectFromGraphQLErrors(error);
    return containsError(errorList, errorCodes);
};
const useResetErrorOnStateChange = (errors, resetErrors, state) => {
    const [prevState, setPrevState] = useState(undefined);
    useEffect(() => {
        if (errors.length !== 0) {
            if (prevState === undefined) {
                setPrevState(state);
            }
            else if (prevState !== state) {
                resetErrors();
                setPrevState(undefined);
            }
        }
    }, [prevState, state]);
};
const shouldUseWarningBanner = (err) => {
    const ERROR_CODES_TO_USE_WARNING_BANNER = [
        blinkConstants.blinkGeneratedErrors.SAGE_INDEXING,
    ];
    return ERROR_CODES_TO_USE_WARNING_BANNER.includes(err?.code);
};
const getMaxLinesForDescription = (tileHeight) => {
    if (tileHeight <= 2)
        return 2;
    if (tileHeight <= 4)
        return 6;
    return undefined;
};
const containsInUpstreamError = (error, errorCodes) => {
    if (!(error && errorCodes))
        return false;
    const errorList = getErrorObjectFromGraphQLErrors(error);
    const upstreamErrorCodes = _.reduce(errorList, (errors, err) => {
        if (err.code !== 'UPSTREAM_FAILURE') {
            return errors;
        }
        errors.push(`${err?.errorObj?.extensions?.exception?.upstreamResponse?.data?.code}`);
        return errors;
    }, []);
    if (upstreamErrorCodes.length <= 0) {
        return false;
    }
    return _.some(upstreamErrorCodes, errCode => errorCodes.includes(errCode));
};
const generateErrorParamMap = (parameters = []) => {
    const errorParamMap = {};
    parameters.forEach((param, index) => {
        errorParamMap[index + 1] = CONNECTOR_DISPLAY_NAME[param]
            ? CONNECTOR_DISPLAY_NAME[param]
            : param;
    });
    return errorParamMap;
};
function getTranslatedStringsForMessageCode(error, defaultErrorMessage, preferSummaryOverDetail = false) {
    if (!error?.code && !defaultErrorMessage)
        return {
            message: '',
            action: '',
        };
    const { code } = error;
    const translatedErrorInfo = getTranslatedInfoForMessageCode(code, defaultErrorMessage);
    const errorMsgInterpolationVars = error?.errorMappingContext ||
        generateErrorParamMap(error?.errorObj?.extensions?.parameters);
    const message = !preferSummaryOverDetail && translatedErrorInfo.detail
        ? t(translatedErrorInfo.detail, errorMsgInterpolationVars)
        : t(translatedErrorInfo.summary, _.defaults(_.omitBy(errorMsgInterpolationVars, _.isNil), {
            0: '',
            1: '',
        }));
    const action = t(translatedErrorInfo.action);
    return {
        message,
        action,
    };
}
function extractErrorCodeAndDebugInfoListFromFirstGraphQLError(errorResponse) {
    const graphQLErrors = errorResponse.graphQLErrors;
    let errorResponseBody;
    if (graphQLErrors && graphQLErrors.length > 0) {
        const firstError = graphQLErrors[0];
        errorResponseBody =
            firstError?.extensions?.response?.body || {};
    }
    return {
        errorTypeCode: errorResponseBody?.error?.message?.code,
        debugInfoList: (errorResponseBody?.error?.message?.debug &&
            JSON.parse(errorResponseBody?.error?.message?.debug)) ||
            [],
    };
}
const reportError = () => {
    if (typeof window.Intercom === 'function') {
        window.Intercom('show');
    }
    else {
        window.open('https://community.thoughtspot.com/customers/s/contactsupport', '_blank', 'noopener');
    }
};
export { containsError, containsInApolloError, getErrorMessage, getErrorObjectFromGraphQLErrors, useResetErrorOnStateChange, getMaxLinesForDescription, shouldUseWarningBanner, containsInUpstreamError, getErrorCodeFromGraphQLErrors, getTranslatedStringsForMessageCode, generateErrorParamMap, extractErrorCodeAndDebugInfoListFromFirstGraphQLError, reportError, };
//# sourceMappingURL=error-utils.js.map